import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useTenantLists, useUpdateTenantList } from "@/hooks/useTenantLists";
import { getApiErrorMessage } from "@/lib/api/api";
import type { TenantListKind } from "@/types/tenant-list";

interface ListSettingsCardProps {
  kind: TenantListKind;
  title: string;
  description: string;
  /** What one entry looks like, so an empty list is not a blank stare. */
  placeholder: string;
  /** False renders it read-only — a cook may read the list, not rewrite it. */
  canManage: boolean;
}

/**
 * One configuration list, edited in place (T-1006).
 *
 * Every save sends the whole list, because the order is the tenant's and an
 * add/remove API cannot express a reorder. Removing an entry does not touch the
 * recipes already using it: their picker keeps offering the stored value, so
 * retiring "Cambro" does not silently rewrite what is in the walk-in.
 */
export function ListSettingsCard({
  kind,
  title,
  description,
  placeholder,
  canManage,
}: ListSettingsCardProps) {
  const { t } = useLingui();
  const { data, isLoading, isError } = useTenantLists();
  const save = useUpdateTenantList();
  const [entry, setEntry] = useState("");

  const values = data?.lists[kind];
  const isSaving = save.isPending && save.variables?.kind === kind;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !values) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{t`This list could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const commit = (next: string[], done?: () => void) =>
    save.mutate(
      { kind, values: next },
      {
        onSuccess: () => {
          done?.();
          toast.success(t`${title} saved`);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to save ${title}`)),
      },
    );

  const add = () => {
    const text = entry.trim();
    if (text === "") return;
    // Adding what is already there would be a no-op with a success toast, which
    // reads as "saved" when nothing was.
    if (values.some((v) => v.toLowerCase() === text.toLowerCase())) {
      toast.error(t`"${text}" is already on this list`);
      return;
    }
    commit([...values, text], () => setEntry(""));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {values.length === 0 ? (
          <p className="text-sm text-(--color-text-secondary)">
            {t`This list is empty, so nothing is offered when filling in a recipe.`}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {values.map((value) => (
              <li
                key={value}
                className="flex items-center gap-1.5 rounded-full border border-(--color-border) bg-(--color-surface-muted) py-1 pl-3 pr-1.5 text-sm"
              >
                <span>{value}</span>
                {canManage && (
                  <button
                    type="button"
                    aria-label={t`Remove ${value}`}
                    disabled={isSaving}
                    onClick={() => commit(values.filter((v) => v !== value))}
                    className="rounded-full p-0.5 text-(--color-text-secondary) hover:bg-(--color-surface-page) hover:text-(--color-text-primary) disabled:opacity-50"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {canManage && (
          <div className="flex items-center gap-2">
            <Input
              aria-label={t`Add to ${title}`}
              value={entry}
              maxLength={60}
              disabled={isSaving}
              placeholder={placeholder}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              className="max-w-xs"
            />
            <Button onClick={add} disabled={entry.trim() === "" || isSaving} className="gap-1.5">
              {isSaving ? <Loader2 className="size-3.5 animate-spin" /> : <Plus className="size-3.5" />}
              {t`Add`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
